import React, { useContext, createContext, useState, useMemo, useRef, useEffect, useCallback } from 'react';
import GlobalLoader from 'components/GlobalLoader';
import { systemAPI } from 'utils/portal-apis';

const LoadingContext = createContext({});

export { GlobalLoader };

/**
 * How long one loading ticket may stay open before the store assumes its owner
 * died and reclaims it. The overlay is a full-viewport, z-index 10000 blocker,
 * so an unreleased ticket used to mean a dead app with no recovery short of a
 * reload — and not even that, when the caller was a mount effect. Long enough
 * for a slow upload on a bad connection; short enough that the worst case is an
 * annoyance instead of an outage.
 */
export const LOADING_TICKET_TIMEOUT_MS = 30000;

/**
 * The loader is a REFERENCE COUNT, not a boolean.
 *
 * It has ~40 independent callers across the app, most of them shaped
 * `setLoading(true) → await … → setLoading(false)`. With a shared boolean the
 * last writer won: whoever finished first switched the overlay off while others
 * were still working, and anyone who threw before their release left it on for
 * everyone, permanently. Counting open work instead makes both impossible — the
 * overlay is visible while at least one ticket is open, and only then.
 */
export const LoaderStore = ({ children }) => {
  const [pending, setPending] = useState(0);
  // id -> timeout handle. Presence in this map IS the ticket: releasing deletes
  // the entry, so a second release is a no-op rather than a double decrement.
  const openTickets = useRef(new Map());

  const release = useCallback(id => {
    if (!openTickets.current.has(id)) return;
    clearTimeout(openTickets.current.get(id));
    openTickets.current.delete(id);
    setPending(count => Math.max(0, count - 1));
  }, []);

  const beginLoading = useCallback(
    (label = 'unlabelled') => {
      const id = Symbol(label);
      const timeout = setTimeout(() => {
        console.warn(
          `[GlobalLoader] "${label}" held a loading ticket for ${LOADING_TICKET_TIMEOUT_MS}ms without releasing it. ` +
            'Reclaiming it so the app stays usable — the owner most likely threw before its release.'
        );
        release(id);
      }, LOADING_TICKET_TIMEOUT_MS);

      openTickets.current.set(id, timeout);
      setPending(count => count + 1);

      return () => release(id);
    },
    [release]
  );

  useEffect(() => {
    const tickets = openTickets.current;
    return () => {
      tickets.forEach(timeout => clearTimeout(timeout));
      tickets.clear();
    };
  }, []);

  const value = useMemo(() => ({ loading: pending > 0, pendingCount: pending, beginLoading }), [pending, beginLoading]);

  return <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>;
};

/**
 * @param {string} [label] names this consumer's ticket in the timeout warning.
 * @returns {{loading: boolean, pendingCount: number, beginLoading: (label?: string) => () => void, setLoading: (on: boolean) => void}}
 *
 * `beginLoading()` is the API to prefer: it returns the single function that
 * releases its ticket, so it pairs with `try/finally` and cannot be left open
 * by an early return.
 *
 * `setLoading(bool)` stays for the existing call sites, but it is now scoped to
 * THIS consumer rather than global: `setLoading(true)` opens at most one ticket
 * per hook instance and `setLoading(false)` releases only that one, so a
 * component can no longer switch the overlay off underneath another component's
 * work. The ticket is also released on unmount, which covers the callers that
 * navigate away mid-load.
 */
export const useGlobalLoader = (label = 'setLoading') => {
  const context = useContext(LoadingContext);
  const { beginLoading } = context;
  const ownTicket = useRef(null);

  const setLoading = useCallback(
    on => {
      if (on) {
        // Re-entrant `setLoading(true)` must not open a second ticket: the
        // matching `setLoading(false)` can only ever release one.
        if (!ownTicket.current && beginLoading) ownTicket.current = beginLoading(label);
        return;
      }
      ownTicket.current?.();
      ownTicket.current = null;
    },
    [beginLoading, label]
  );

  useEffect(
    () => () => {
      ownTicket.current?.();
      ownTicket.current = null;
    },
    []
  );

  return useMemo(() => ({ ...context, setLoading }), [context, setLoading]);
};

export const ThemeContext = createContext({
  content: {
    modals: {
      streamSetup: {
        title: 'Stream Setup',
        videoProvideQuestion: 'How would you like to provide video?',
        easyMode: 'Right here on this page (Easy).',
        advancedMode: 'Other streaming software, such as OBS (Advanced).',
        rtmpInstructions: 'Copy the RTMP settings below to your streaming software of choice.',
        rtmpWarning: 'The host of this stream has selected RTMP publishing mode.',
        streamingPlatform: 'Streaming to another platform?',
        rtmpDestination: 'RTMP Destination',
        rtmpTooltip: 'You can send your stream to other platforms.',
        previewWarning: 'We will start sending video for preview.',
      },
      createStream: {
        title: 'Create a Live Stream',
        success: 'Stream Created!',
        appMessage: 'Your event has been added to the app!',
      },
    },
  },
});
export const useGlobalTheme = () => {
  return useContext(ThemeContext);
};

export const checkVersion = async version => {
  const dbVersion = await systemAPI.getFrontendVersion();

  if (!dbVersion) {
  } else {
    if (dbVersion !== version) {
      alert('There is an updated version of HotMic update available, this page will reload.');
      window.location.reload(true);
    } else {
    }
  }
};
